import { PEPModal } from '@dealstack/frontend/shared/components/PEPModal/PEPModal';
import { Circle } from '@dealstack/frontend/shared/components/common/Circle';
import { GreyBox } from '@dealstack/frontend/shared/components/common/GreyBox';
import {
	TEST_ELEMENT_IDS,
	INVESTOR_TEST_ELEMENT_ID,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { ConfirmationModalV3 } from '@dealstack/frontend/shared/components/ConfirmationModal/ConfirmationModalV3';
import { CheckboxGroupV2 } from '@dealstack/frontend/shared/components/forms/CheckboxGroup/CheckboxGroup.v2';
import { FormCheckbox } from '@dealstack/frontend/shared/components/forms/FormCheckbox/FormCheckbox';
import { TextArea } from '@dealstack/frontend/shared/components/forms/TextArea/TextArea';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { LastVerifiedBadge } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/LastVerifiedBadge';
import {
	TransInvestor,
	useTranslationInvestor,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	Button,
	Checkbox as MantineCheckbox,
	Divider,
	Flex,
	Text as MantineText,
	Title,
} from '@mantine/core';
import { FormProvider } from 'react-hook-form';

import {
	DECLARATIONS_FORM,
	FUNDS_CHECKBOXES,
	SOURCE_OF_FUNDS_TYPE,
	useJoinAndAllocationDeclarationsController,
} from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationChecksComplete/JoinAndAllocationDeclarations/useJoinAndAllocationDeclarationsController';
import {
	DeclarationsWrapper,
	SingleDeclarationWrapper,
	SummarySection,
} from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationChecksComplete/JoinAndAllocationDeclarations/styled';

export const JoinAndAllocationDeclarations = () => {
	const tInvestor = useTranslationInvestor();

	const {
		declarationsForm,
		isPEPModalVisible,
		setPEPModalVisible,
		onSubmit,
		isPIVInvestment,
		isDeclarationsRejected,
		rejectionComment,
		showConfirmModal,
		onCloseConfirmationModal,
		onConfirmSubmit,
		isDeclarationsLoaded,
		isInheritedAndLocked,
		onConfirmAllDeclarations,
		onDismissInherited,
		onResetInherited,
		declarationsCreatedAt,
		hasInheritedDeclarations,
		isConfirmedInherited,
		isBeneficialOwnershipRequired,
		isSourceOfFundsRequired,
		isPepRequired,
	} = useJoinAndAllocationDeclarationsController();

	if (!isDeclarationsLoaded) {
		return null;
	}

	const isPep = declarationsForm.watch(DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON);
	const showSourceOfFundsTextarea = !!declarationsForm
		.watch(DECLARATIONS_FORM.SOURCE_OF_FUNDS)
		?.includes(SOURCE_OF_FUNDS_TYPE.OTHER);

	// Section numbers shift when some declarations are not required.
	let sectionCounter = 0;
	const boSectionNumber = isBeneficialOwnershipRequired ? ++sectionCounter : null;
	const sofSectionNumber = isSourceOfFundsRequired ? ++sectionCounter : null;
	const pepSectionNumber = isPepRequired ? ++sectionCounter : null;

	const renderInheritedSummary = () => {
		const formValues = declarationsForm.getValues();
		const selectedFunds = (formValues[DECLARATIONS_FORM.SOURCE_OF_FUNDS] ?? []).map((fund) => {
			const checkbox = FUNDS_CHECKBOXES.find((c) => c.name === fund);
			return checkbox ? tInvestor(checkbox.label) : fund;
		});

		const hasInheritedBo = formValues[DECLARATIONS_FORM.BENEFICIAL_OWNERSHIP] !== undefined;
		const hasInheritedPep =
			formValues[DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON] !== undefined;
		const hasInheritedSof = selectedFunds.length > 0;

		return (
			<>
				{isBeneficialOwnershipRequired && hasInheritedBo && (
					<SummarySection>
						<MantineText size="sm" c="dark.0">
							{tInvestor('investment.join.declaration.beneficial_ownership')}
						</MantineText>
						<MantineText fw={600}>
							{formValues[DECLARATIONS_FORM.BENEFICIAL_OWNERSHIP]
								? tInvestor('common.yes')
								: tInvestor('common.no')}
						</MantineText>
					</SummarySection>
				)}

				{isPepRequired && hasInheritedPep && (
					<SummarySection>
						<MantineText size="sm" c="dark.0">
							{tInvestor('investment.join.declaration.pep')}
						</MantineText>
						<MantineText fw={600}>
							{formValues[DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON]
								? tInvestor('investment.join.kyc.declarations.pep.label_2')
								: tInvestor('investment.join.kyc.declarations.pep.label_1')}
						</MantineText>
					</SummarySection>
				)}

				{isSourceOfFundsRequired && hasInheritedSof && (
					<SummarySection>
						<MantineText size="sm" c="dark.0">
							{tInvestor('investment.join.declaration.source_funds')}
						</MantineText>
						{selectedFunds.map((fund) => (
							<MantineText key={fund} fw={600}>
								• {fund}
							</MantineText>
						))}
					</SummarySection>
				)}
			</>
		);
	};

	return (
		<div style={{ maxWidth: '40rem', whiteSpace: 'pre-line' }}>
			{hasInheritedDeclarations && declarationsCreatedAt && (
				<LastVerifiedBadge date={declarationsCreatedAt} />
			)}
			{hasInheritedDeclarations && (
				<Flex direction="column" gap="0.25rem" mt="1rem">
					<MantineText fw={700}>
						{tInvestor('investment.join.kyc.inherited.title')}
					</MantineText>
					<MantineText size="sm" c="dark.0">
						{tInvestor('investment.join.kyc.inherited.description')}
					</MantineText>
				</Flex>
			)}
			{isInheritedAndLocked && (
				<DeclarationsWrapper>
					<Divider />
					{renderInheritedSummary()}

					<Flex gap="0.5rem">
						<Button
							size="sm"
							variant="outline"
							color="dark"
							onClick={onDismissInherited}
							data-testid={
								INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_INHERITED_DISMISS_BUTTON
							}
						>
							{tInvestor('investment.join.kyc.inherited.no')}
						</Button>
						<Button
							size="sm"
							variant="filled"
							color="blue"
							onClick={onConfirmAllDeclarations}
							leftSection={<Icon variant="tick" size="1rem" />}
							data-testid={
								INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_INHERITED_CONFIRM_BUTTON
							}
						>
							{tInvestor('investment.join.kyc.inherited.yes')}
						</Button>
					</Flex>
				</DeclarationsWrapper>
			)}
			{isConfirmedInherited && (
				<DeclarationsWrapper>
					<Divider />
					{renderInheritedSummary()}

					<Flex>
						<Button
							size="sm"
							variant="outline"
							color="dark"
							onClick={onResetInherited}
							data-testid={
								INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_INHERITED_DISMISS_BUTTON
							}
						>
							{tInvestor('investment.join.kyc.inherited.reset')}
						</Button>
					</Flex>
				</DeclarationsWrapper>
			)}
			{!isInheritedAndLocked && !isConfirmedInherited && (
				<FormProvider {...declarationsForm}>
					<form onSubmit={declarationsForm.handleSubmit(onSubmit)}>
						<MantineText>{tInvestor('investment.join.declaration.descr')}</MantineText>

						<DeclarationsWrapper>
							{isDeclarationsRejected && (
								<Text variant="p-lg" colorError>
									{tInvestor('investment.join.kyc.declarations.rejected')}
									{!!rejectionComment &&
										tInvestor(
											'investment.join.kyc.declarations.rejected_comment',
											{ rejectionComment }
										)}
								</Text>
							)}

							{isBeneficialOwnershipRequired && (
								<SingleDeclarationWrapper>
									<Flex align="center" gap="0.5rem">
										<Circle variant="filled">
											<MantineText c="gray.0" fz="lg" fw={600}>
												{boSectionNumber}
											</MantineText>
										</Circle>
										<Title order={5}>
											{tInvestor(
												'investment.join.declaration.beneficial_ownership'
											)}
										</Title>
									</Flex>

									<GreyBox>
										<UIElementIdentifier
											identifier={
												INVESTOR_TEST_ELEMENT_ID.INVESTOR_DECLARATIONS_BENEFICIAL_OWNERSHIP_CHECKBOX
											}
										>
											<FormCheckbox
												name={DECLARATIONS_FORM.BENEFICIAL_OWNERSHIP}
												label={tInvestor(
													`investment.join.kyc.declarations.beneficial.${
														isPIVInvestment
															? 'label_piv'
															: 'label_individual'
													}`
												)}
											/>
										</UIElementIdentifier>
									</GreyBox>
								</SingleDeclarationWrapper>
							)}

							{isSourceOfFundsRequired && (
								<SingleDeclarationWrapper>
									<Flex align="center" gap="0.5rem">
										<Circle variant="filled">
											<MantineText c="gray.0" fz="lg" fw={600}>
												{sofSectionNumber}
											</MantineText>
										</Circle>
										<Title order={5}>
											{tInvestor('investment.join.declaration.source_funds')}
										</Title>
									</Flex>

									<GreyBox>
										<Title order={5} sx={{ marginBottom: '0.5rem' }}>
											{tInvestor(
												'investment.join.declaration.source_funds_descr'
											)}
										</Title>
										<UIElementIdentifier
											identifier={
												INVESTOR_TEST_ELEMENT_ID.INVESTOR_DECLARATIONS_SOURCE_OF_FUNDS_CHECKBOX
											}
										>
											<CheckboxGroupV2
												name={DECLARATIONS_FORM.SOURCE_OF_FUNDS}
											>
												<Flex direction="column" gap="0.5rem">
													{FUNDS_CHECKBOXES.map((checkbox) => (
														<UIElementIdentifier
															key={checkbox.name}
															identifier={
																checkbox.testId as INVESTOR_TEST_ELEMENT_ID
															}
														>
															<MantineCheckbox
																value={checkbox.name}
																label={tInvestor(checkbox.label)}
															/>
														</UIElementIdentifier>
													))}
												</Flex>
											</CheckboxGroupV2>
										</UIElementIdentifier>

										{showSourceOfFundsTextarea && (
											<UIElementIdentifier
												identifier={
													INVESTOR_TEST_ELEMENT_ID.INVESTOR_DECLARATIONS_SOURCE_OF_FUNDS_OTHER_DETAILS
												}
											>
												<TextArea
													label={tInvestor(
														'investment.join.kyc.declarations.pep.details_label'
													)}
													name={
														DECLARATIONS_FORM.SOURCE_OF_FUNDS_OTHER_DETAILS
													}
													rows={2}
													fullWidth
												/>
											</UIElementIdentifier>
										)}
									</GreyBox>
								</SingleDeclarationWrapper>
							)}

							{isPepRequired && (
								<SingleDeclarationWrapper>
									<Flex align="center" gap="0.5rem">
										<Circle variant="filled">
											<MantineText c="gray.0" fz="lg" fw={600}>
												{pepSectionNumber}
											</MantineText>
										</Circle>
										<Title order={5}>
											{tInvestor('investment.join.declaration.pep')}
										</Title>
									</Flex>
									<MantineText>
										<TransInvestor
											components={{
												open: (
													<Text
														i18NextInline
														colorLink
														isLink
														bold
														variant="caption"
														onClick={() => setPEPModalVisible(true)}
													/>
												),
											}}
											i18nKey="investment.join.kyc.declarations.pep.subtitle"
										/>
									</MantineText>

									<GreyBox>
										<Title order={5} sx={{ marginBottom: '0.5rem' }}>
											{tInvestor('investment.join.declaration.pep_question')}
										</Title>

										<UIElementIdentifier
											identifier={
												INVESTOR_TEST_ELEMENT_ID.INVESTOR_DECLARATIONS_PEP_PERSON_CHECKBOX
											}
										>
											<Flex direction="column" gap="0.5rem">
												<UIElementIdentifier
													identifier={
														INVESTOR_TEST_ELEMENT_ID.NOT_PEP_CHECKBOX
													}
												>
													<MantineCheckbox
														checked={isPep === false}
														onClick={() =>
															declarationsForm.setValue(
																DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON,
																isPep === false ? undefined : false,
																{ shouldValidate: true }
															)
														}
														label={tInvestor(
															'investment.join.kyc.declarations.pep.label_1'
														)}
														disabled={declarationsForm.watch(
															DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON
														)}
													/>
												</UIElementIdentifier>
												<UIElementIdentifier
													identifier={
														INVESTOR_TEST_ELEMENT_ID.IS_PEP_CHECKBOX
													}
												>
													<MantineCheckbox
														checked={isPep}
														onClick={() =>
															declarationsForm.setValue(
																DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON,
																isPep ? undefined : true,
																{ shouldValidate: true }
															)
														}
														label={tInvestor(
															'investment.join.kyc.declarations.pep.label_2'
														)}
														disabled={
															declarationsForm.watch(
																DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON
															) === false
														}
													/>
												</UIElementIdentifier>

												{!!declarationsForm.formState.errors[
													DECLARATIONS_FORM.IS_POLITICALLY_EXPOSED_PERSON
												]?.message && (
													<MantineText c="red.6">
														{
															declarationsForm.formState.errors[
																DECLARATIONS_FORM
																	.IS_POLITICALLY_EXPOSED_PERSON
															]?.message
														}
													</MantineText>
												)}
											</Flex>
										</UIElementIdentifier>
										{isPep && (
											<UIElementIdentifier
												identifier={
													INVESTOR_TEST_ELEMENT_ID.INVESTOR_DECLARATIONS_PEP_PERSON_DETAILS
												}
											>
												<TextArea
													label={tInvestor(
														'investment.join.kyc.declarations.pep.details_label'
													)}
													name={DECLARATIONS_FORM.PEP_DETAILS}
													rows={4}
													fullWidth
												/>
											</UIElementIdentifier>
										)}
									</GreyBox>
								</SingleDeclarationWrapper>
							)}
						</DeclarationsWrapper>
					</form>
				</FormProvider>
			)}
			{isPEPModalVisible ? <PEPModal onClose={() => setPEPModalVisible(false)} /> : null}
			{showConfirmModal && (
				<ConfirmationModalV3
					title={tInvestor('investment.continue_confirm_modal.title')}
					description={tInvestor('investment.continue_confirm_modal.subtitle')}
					children={
						<Flex gap="xs">
							<UIElementIdentifier
								identifier={
									TEST_ELEMENT_IDS.ADMIN_DASHBOARD_CONFIRMATION_MODAL_CANCEL_BUTTON
								}
							>
								<Button
									size="md"
									variant="secondary"
									onClick={onCloseConfirmationModal}
								>
									{tInvestor('investment.continue_confirm_modal.back')}
								</Button>
							</UIElementIdentifier>
							<UIElementIdentifier
								identifier={
									TEST_ELEMENT_IDS.ADMIN_DASHBOARD_CONFIRMATION_MODAL_CONTINUE_BUTTON
								}
							>
								<Button size="md" variant="subtle" onClick={onConfirmSubmit}>
									{tInvestor('investment.continue_confirm_modal.continue')}
								</Button>
							</UIElementIdentifier>
						</Flex>
					}
				/>
			)}
		</div>
	);
};
